'use memo';

import {
  addDividersBetweenElements,
  AgxBodyText,
  AgxButton,
  AgxColumn,
  AgxDivider,
  AgxHeader,
  AgxRow,
  AgxSlideUpModal,
  Images,
  StakeholderType,
  DocumentTypes,
  DocumentTypesMap,
  EnvelopeSubmissionStatus,
  EnvelopeSubmissionType,
  MultiOptionButtonOption,
  AgxLabel,
  QldContractStage,
  NswContractStage,
  FormConfigType,
  EnvelopeRecipientStatus,
  UploadedDocument as OldUploadedDocument,
} from '@urbanx/agx-ui-components';
import {
  AgxButton as AgxButtonV2,
  AgxModal,
  AgxSpacer as AgxSpacerV2,
  AgxTypography,
  Icon_AlertCircleOutline,
  Icon_CheckmarkCircleOutline,
} from '@urbanx/agx.ui-components2';
import dayjs from 'dayjs';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { isEqual } from 'lodash-es';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { cleanTwoLineAddress } from 'utils/formatAddress';
import FormDetailsHeader from '../../Common/FormDetailsHeader';
import placeHolderImage from 'assets/images/placeholder-property.png';
import { Signatory } from '../../../CampaignsList/components/SentForSigningPanel/Signatory';
import { completeCampaignStep } from 'Api/Campaigns/campaignsApi';
import { AuthContext } from '@urbanx/agx.auth.client';
import { Breakpoints, ScreenSize } from 'utils/screen';
import { setAndShowErrorToast } from 'store/config';
import { useGetFileLink } from 'components/ui-components/File/fileApi';
import { getEnumValue } from 'helpers/enumHelper';
import {
  selectContract,
  removeFormSubmission,
} from 'features/campaigns/campaignsReducer';
import { prepareSignatories, getAllBuyerNames } from 'utils/vendorBuyerUtil';
import { formatListOfStrings } from 'utils/formatString';
import { formatCurrencyWithConditionalDecimals } from 'utils/formatNumber';
import { useFormLoader } from 'hooks/useFormLoader';
import { Tabs } from 'types/Tabs';
import SendToVendorsConfirmation from './SendToVendorsConfirmation';
import './ContractRequestSigningPanel.scss';
import { openFileInNewTab } from 'utils/openFileInNewTab';
import { sortDocumentsByType } from 'utils/documentUtil';
import FileList from '../Common/FileList';
import FileUpload from 'features/elements/FileUpload/FileUpload';
import { docusignApi } from 'Api/DocuSign/docusignApi';
import {
  CampaignDetailModel,
  CampaignDetailModelState,
  CoolingOffType,
  EnvelopeSubmissionRecipient,
  FormType,
  InProgressFormModelFormType,
  NoCoolingOffReason,
  UploadedDocument,
  UploadedDocumentDocumentType,
  useExchangeContract,
  useInvalidateUseGetCampaign,
} from '@urbanx/agx.campaigns.client';
import { useNewSelectedContract } from 'hooks/useNewSelectedContract';

interface ContractRequestSigningPanelProps {
  campaign: CampaignDetailModel;
  currentTab: Tabs;
  mobile?: boolean;
}

const ContractRequestSigningPanel = ({
  campaign,
  currentTab,
  mobile = false,
}: ContractRequestSigningPanelProps) => {
  const { getAuthToken } = useContext(AuthContext);
  const isDesktop = ScreenSize() === Breakpoints.Desktop;
  const { loadForm, loadFormWithConfig } = useFormLoader();
  const selectedContract = useNewSelectedContract();
  const {
    address,
    state,
    propertyImageUrls: { medium: imageUrl = null } = {},
    campaignId,
  } = campaign;
  const { buyers, salePrice, lastUpdate, envelopeSubmission, contractStage } =
    selectedContract ?? {};
  const {
    envelopeSubmissionId: envelopeId,
    status: envelopeSubmissionStatus,
    type: envelopeSubmissionType,
    recipients,
  } = envelopeSubmission ?? {};

  const contractSent =
    envelopeSubmissionStatus &&
    [
      EnvelopeSubmissionStatus.Signed,
      EnvelopeSubmissionStatus.Complete,
      EnvelopeSubmissionStatus.Sent,
    ].includes(envelopeSubmissionStatus);

  const headerDetail = `${contractSent ? 'Sent' : 'Last updated'} ${dayjs(
    lastUpdate
  ).format('DD/MM/YY')}`;
  const availableForms = useAppSelector(state => state.forms.availableForms);
  const [disableInputs, setDisableInputs] = useState(false);
  const [disableSendToVendors, setDisableSendToVendors] = useState(false);
  const [showVoidEditConfirmationModal, setShowVoidEditConfirmationModal] =
    useState(false);
  const [
    showExchangeConfirmationModal,
    setShowExchangeConfirmationModal,
  ] = useState(false);
  const [
    waiverOfCoolingOffDocuments,
    setWaiverOfCoolingOffDocuments,
  ] = useState<OldUploadedDocument[]>([]);
  const [showExchangeSuccessModal, setShowExchangeSuccessModal] =
    useState(false);
  const [
    showSendVendorsConfirmationModal,
    setShowSendVendorsConfirmationModal,
  ] = useState(false);
  const exchangeSuccessCloseTimeout =
    useRef<ReturnType<typeof setTimeout> | null>(null);
  const [addressLineOne, addressLineTwo] = cleanTwoLineAddress(address);
  const isMobile = ScreenSize() === Breakpoints.Mobile;
  const dispatch = useAppDispatch();
  const invalidateCampaign = useInvalidateUseGetCampaign();
  const { exchangeContract, exchangeContractPending } =
    useExchangeContract({
      onSuccess: () => {},
      onError: () => {},
    });
  const approver = recipients?.find(
    r => r.stakeholderType === StakeholderType.Approver
  );
  const vendors = prepareSignatories(campaign.vendors, StakeholderType.Vendor);
  const allBuyers = prepareSignatories(buyers, StakeholderType.Buyer);
  const allVendorSolicitors = prepareSignatories(
    buyers,
    StakeholderType.VendorSolicitor
  );
  const allBuyerSolicitors = prepareSignatories(
    buyers,
    StakeholderType.BuyerSolicitor
  );
  const allSignatories = [
    ...allBuyers,
    ...vendors,
    ...allVendorSolicitors,
    ...allBuyerSolicitors,
  ];

  const declinedStatus = recipients?.find(
    recipient => recipient.status === EnvelopeRecipientStatus.Declined
  );

  const bouncedStatus = recipients?.find(
    recipient => recipient.status === EnvelopeRecipientStatus.Bounced
  );

  const allSigned = (
    recipients: EnvelopeSubmissionRecipient[],
    stakeHolderType: StakeholderType
  ) => {
    const stakeHolders = recipients.filter(
      x => x.stakeholderType === stakeHolderType
    );
    const validStatuses = [
      EnvelopeRecipientStatus.Signed,
      EnvelopeRecipientStatus.Complete,
    ];
    return (
      stakeHolders.length > 0 &&
      stakeHolders.every(x => validStatuses.includes(x.status!))
    );
  };

  const AllBuyersSigned = allSigned(recipients ?? [], StakeholderType.Buyer);
  const AllVendorsSigned = allSigned(recipients ?? [], StakeholderType.Vendor);
  const getFileLink = useGetFileLink();
  const isContractExchange =
    selectedContract?.inProgressForm?.formType ===
    InProgressFormModelFormType.ContractExchange;
  const existingWaiverOfCoolingOffDocument = selectedContract?.documents?.find(
    document =>
      document.documentType === UploadedDocumentDocumentType.WaiverOfCoolingOff
  );
  const waiverOfCoolingOffRequired =
    selectedContract?.buyerCoolingOffDetails?.coolingOffType ===
      CoolingOffType.NoCoolingOff &&
    selectedContract?.buyerCoolingOffDetails?.noCoolingOffReason ===
      NoCoolingOffReason.Waived;
  const shouldUploadWaiverOfCoolingOff =
    isContractExchange &&
    waiverOfCoolingOffRequired &&
    !existingWaiverOfCoolingOffDocument;
  const uploadedWaiverOfCoolingOff = waiverOfCoolingOffDocuments[0] as unknown as
    | UploadedDocument
    | undefined;

  useEffect(() => {
    return () => {
      if (exchangeSuccessCloseTimeout.current) {
        clearTimeout(exchangeSuccessCloseTimeout.current);
        exchangeSuccessCloseTimeout.current = null;
      }
    };
  }, []);

  const fileDownloadOptions = useMemo(() => {
    if ((selectedContract?.documents?.length ?? 0) === 0) return [];
    const options: MultiOptionButtonOption[] = [];
    const miscDocumentTypes = [
      DocumentTypes.Miscellaneous,
      DocumentTypes.AdditionalAnnexure,
    ];

    const orderedDocuments = sortDocumentsByType(
      selectedContract?.documents as unknown as OldUploadedDocument[]
    );

    const nonMiscDocuments = orderedDocuments.filter(
      (d, ind, arr) =>
        !miscDocumentTypes.includes(d.documentType) &&
        (!ind || d.documentType !== arr[ind - 1]?.documentType)
    );
    const miscDocuments = orderedDocuments.filter(d =>
      miscDocumentTypes.includes(d.documentType)
    );

    nonMiscDocuments.forEach(d => {
      options.push({
        text: DocumentTypesMap[d.documentType as keyof typeof DocumentTypesMap],
        documentType: d.documentType,
        onClick: async () => {
          const fileLink = await getFileLink(d.containerFilePath);
          if (fileLink != null) {
            openFileInNewTab(isDesktop, fileLink);
          }
        },
      });
    });

    miscDocuments.forEach(d => {
      options.push({
        text: d.fileName,
        documentType: d.documentType,
        onClick: async () => {
          const fileLink = await getFileLink(d.containerFilePath);
          if (fileLink != null) {
            openFileInNewTab(isDesktop, fileLink);
          }
        },
      });
    });

    return options;
  }, [selectedContract?.documents, getFileLink]);

  const contractExchangeDownloadOption =
    fileDownloadOptions.find(
      file => file.documentType === DocumentTypes.ContractExchange
    ) ??
    fileDownloadOptions.find(
      file => file.documentType !== DocumentTypes.TitleSearch
    );

  const beginSubmission = async () => {
    const contractSubmissionForm = availableForms.find(
      form =>
        form.type === FormType.ContractSubmission &&
        isEqual(form.state, campaign.state)
    );

    if (contractSubmissionForm === null) return;

    try {
      setDisableInputs(true);
      const authToken = await getAuthToken();

      if (!authToken || !selectedContract) return;

      const newForm = await completeCampaignStep(
        authToken,
        campaignId ?? '',
        false,
        selectedContract.contractId
      );

      if (!newForm) {
        dispatch(setAndShowErrorToast('Failed to start Contract submission'));
        setDisableInputs(false);
        return;
      }

      await loadFormWithConfig(newForm);
    } catch (err) {
      dispatch(setAndShowErrorToast('Failed to start Contract submission'));
      setDisableInputs(false);
    }
  };

  const sendToVendors = async () => {
    setDisableInputs(true);
    setDisableSendToVendors(true);
    try {
      const authToken = await getAuthToken();

      if (!authToken || !selectedContract) return;

      await docusignApi(authToken).post('ApproveEnvelope', {
        campaignId: campaignId,
        formId: selectedContract.contractId,
        envelopeId: envelopeId,
      });
      setShowSendVendorsConfirmationModal(false);
      setDisableInputs(false);
    } catch (err) {
      dispatch(setAndShowErrorToast('Failed to send contract to vendors'));
      setDisableInputs(false);
      setDisableSendToVendors(false);
    }
  };

  const allRecipients =
    recipients?.filter(
      signatory =>
        signatory.stakeholderType !== StakeholderType.Approver &&
        (!approver || signatory.stakeholderType !== StakeholderType.Vendor)
    ) ?? [];

  const allRecipientsInfo = allRecipients.map(recipient => {
    const allSignatoriesInfo = allSignatories.find(
      signatory => recipient.recipientId === signatory.id
    );

    if (allSignatoriesInfo) {
      return { ...recipient, name: allSignatoriesInfo.name };
    }

    return recipient;
  });

  const renderSignatories = () => {
    const signatoryComponents = allRecipientsInfo.map((signatory, i) => {
      if (
        signatory &&
        envelopeId &&
        ((envelopeSubmissionType === EnvelopeSubmissionType.DocuSign &&
          (!approver ||
            signatory.stakeholderType !== StakeholderType.Vendor)) ||
          envelopeSubmissionType === EnvelopeSubmissionType.Email)
      ) {
        return (
          <Signatory
            key={`signatory-${i}`}
            signatory={signatory}
            envelopeSubmissionType={envelopeSubmissionType}
            envelopeId={envelopeId}
            declinedStatus={!!(declinedStatus || bouncedStatus)}
          />
        );
      }

      return <></>;
    });

    return [
      <AgxDivider key="initialDivider" />,
      ...addDividersBetweenElements(signatoryComponents, false, false),
    ];
  };

  const onEditContract = async () => {
    if (!selectedContract) return;

    if (envelopeSubmissionType === EnvelopeSubmissionType.Email) {
      await loadForm(
        campaignId ?? '',
        selectedContract.contractId ?? '',
        FormConfigType.Campaign
      );
    } else if (envelopeSubmissionType === EnvelopeSubmissionType.DocuSign) {
      setShowVoidEditConfirmationModal(true);
    }
  };

  const onVoidAndEdit = async () => {
    if (!selectedContract) return;

    setDisableInputs(true);

    try {
      const authToken = await getAuthToken();

      if (!authToken || !envelopeId) return;

      await removeFormSubmission(
        authToken,
        campaignId ?? '',
        envelopeId,
        selectedContract.contractId
      );

      await loadForm(
        campaignId ?? '',
        selectedContract.contractId ?? '',
        FormConfigType.Campaign
      );
    } catch (err) {
      console.error(err);
      dispatch(setAndShowErrorToast('Failed to void envelope'));
    }

    setDisableInputs(false);
  };

  const closeExchangeModal = () => {
    if (exchangeSuccessCloseTimeout.current) {
      clearTimeout(exchangeSuccessCloseTimeout.current);
      exchangeSuccessCloseTimeout.current = null;
    }

    setShowExchangeConfirmationModal(false);
    setShowExchangeSuccessModal(false);
    setWaiverOfCoolingOffDocuments([]);
    setDisableInputs(false);
  };

  const completeExchangeContract = async () => {
    if (!campaignId || !selectedContract?.contractId) return;

    try {
      setDisableInputs(true);

      await exchangeContract({
        campaignId,
        contractId: selectedContract.contractId,
        waiverOfCoolingOff: uploadedWaiverOfCoolingOff,
      });

      setShowExchangeSuccessModal(true);

      exchangeSuccessCloseTimeout.current = setTimeout(async () => {
        setShowExchangeConfirmationModal(false);
        setShowExchangeSuccessModal(false);
        setWaiverOfCoolingOffDocuments([]);
        setDisableInputs(false);
        exchangeSuccessCloseTimeout.current = null;
        await invalidateCampaign(campaignId);
      }, 2000);
    } catch (err) {
      console.error(err);
      dispatch(setAndShowErrorToast('Failed to exchange Contract'));
      setDisableInputs(false);
    }
  };

  const SubmitContractButton = () => {
    return (
      <AgxButton
        primary
        wide={mobile}
        large
        text={isContractExchange ? 'Exchange Contract' : 'Start Submission'}
        disabled={
          disableInputs ||
          exchangeContractPending ||
          (envelopeSubmissionType === EnvelopeSubmissionType.DocuSign &&
            !(AllBuyersSigned && AllVendorsSigned)) ||
          (envelopeSubmissionType === EnvelopeSubmissionType.Email &&
            envelopeSubmissionStatus !== EnvelopeSubmissionStatus.Sent)
        }
        onClick={
          isContractExchange
            ? () => setShowExchangeConfirmationModal(true)
            : beginSubmission
        }
        shrinkOnLargerDevices
      />
    );
  };

  const DownloadContractExchangeButton = () => {
    return (
      <AgxButton
        hollow
        wide={mobile}
        large={!isMobile}
        small={isMobile}
        text="Download"
        onClick={contractExchangeDownloadOption?.onClick}
        shrinkOnLargerDevices
        disabled={
          disableInputs ||
          exchangeContractPending ||
          !contractExchangeDownloadOption
        }
      />
    );
  };

  const DocuSignVendorButton = () => {
    return (
      <div className={isMobile ? 'campaignModalButtons' : ''}>
        <AgxButton
          primary
          wide={mobile}
          large
          text="Send to Vendors"
          onClick={() => setShowSendVendorsConfirmationModal(true)}
          shrinkOnLargerDevices
          disabled={
            (!AllBuyersSigned && approver !== undefined) ||
            disableSendToVendors ||
            disableInputs
          }
        />
      </div>
    );
  };

  const EditContractButton = () => {
    return (
      <AgxButton
        hollow
        wide={mobile}
        large={!isMobile}
        small={isMobile}
        text="Edit"
        onClick={onEditContract}
        shrinkOnLargerDevices
        disabled={disableInputs || (AllBuyersSigned && AllVendorsSigned)}
      />
    );
  };

  const voidEditConfirmationDialogBody = (
    <AgxColumn veryLargeGap extraClasses={'confirmationModalStyle'}>
      <Images.AlertCircleOutline />
      <AgxHeader size={3}>
        Editing contract will void current DocuSign envelope
      </AgxHeader>
      <AgxBodyText medium>
        Current recipients will be notified, you'll be able to generate a new
        contract and send a new DocuSign envelope.
      </AgxBodyText>
      <AgxBodyText medium>Continue to edit?</AgxBodyText>
      {isMobile && <AgxDivider />}
      <div className={'confirmationButtonContainerStyle'}>
        <AgxButton
          id="startsubmission"
          large
          primary
          text="Void and Edit"
          onClick={onVoidAndEdit}
          disabled={disableInputs}
        />
        <AgxButton
          id="cancel"
          hollow
          large
          text="No, go back"
          onClick={() => setShowVoidEditConfirmationModal(false)}
          disabled={disableInputs}
        />
      </div>
    </AgxColumn>
  );

  const confirmSendToVendorsDialogBody = (
    <AgxColumn veryLargeGap extraClasses={'confirmationModalStyle'}>
      <AgxHeader size={3}>Vendor Signatories</AgxHeader>
      <SendToVendorsConfirmation
        vendors={vendors}
        onBack={() => setShowSendVendorsConfirmationModal(false)}
        onSend={sendToVendors}
        disabled={disableInputs}
      />
    </AgxColumn>
  );

  const exchangeModalIcon = showExchangeSuccessModal ? (
    <div className="contractExchangeModalIcon success">
      <Icon_CheckmarkCircleOutline
        width={40}
        height={40}
        color="var(--fg-success)"
      />
    </div>
  ) : (
    <div className="contractExchangeModalIcon warning">
      <Icon_AlertCircleOutline type="Warning" width={40} height={40} />
    </div>
  );

  const exchangeConfirmationContent = showExchangeSuccessModal ? null : (
    <AgxSpacerV2
      direction="vertical"
      gap="spacing-lg"
      padding={{ left: 'xl', right: 'xl', bottom: 'spacing-3xl' }}
      className="contractExchangeModalContent"
    >
      <AgxTypography
        id="contract-exchange-deposit-warning"
        type="body_ui"
        size="small"
        color="var(--text-secondary)"
      >
        Deposit must be paid before the Contract can be Exchanged.
      </AgxTypography>
      <AgxTypography
        id="contract-exchange-dated-warning"
        type="body_ui"
        size="small"
        color="var(--text-secondary)"
      >
        Once confirmed, the Contract will be dated.
      </AgxTypography>
      {shouldUploadWaiverOfCoolingOff && (
        <AgxSpacerV2
          direction="vertical"
          gap="spacing-sm"
          className="contractExchangeWaiverUpload"
        >
          <AgxSpacerV2
            direction="horizontal"
            contentAlignment="center"
            gap="spacing-2xs"
          >
            <AgxTypography
              id="contract-exchange-66w-upload-label"
              type="body_ui"
              size="small"
            >
              66W Required to Exchange
            </AgxTypography>
            <AgxTypography
              id="contract-exchange-66w-upload-required"
              type="body_ui"
              size="small"
              color="var(--fg-error)"
            >
              *
            </AgxTypography>
          </AgxSpacerV2>
          <FileUpload
            id="contract-exchange-66w"
            title="66W Required to Exchange"
            campaignId={campaignId}
            contractId={selectedContract?.contractId}
            documentType={DocumentTypes.WaiverOfCoolingOff}
            extensions={['pdf']}
            hideLabel
            validate={false}
            required
            onValueChanged={({ value }) =>
              setWaiverOfCoolingOffDocuments(value ?? [])
            }
          />
        </AgxSpacerV2>
      )}
    </AgxSpacerV2>
  );

  const exchangeConfirmationActions = showExchangeSuccessModal ? null : (
    <AgxSpacerV2
      direction="horizontal"
      contentJustification="center"
      gap="spacing-lg"
      fill
    >
      <AgxButtonV2
        id="cancel-contract-exchange"
        variant="hollow"
        size="large"
        text="Cancel"
        onClick={closeExchangeModal}
        disabled={disableInputs || exchangeContractPending}
        stretch
      />
      <AgxButtonV2
        id="confirm-contract-exchange"
        variant="primary"
        size="large"
        text="Confirm"
        onClick={completeExchangeContract}
        disabled={
          disableInputs ||
          exchangeContractPending ||
          (shouldUploadWaiverOfCoolingOff && !uploadedWaiverOfCoolingOff)
        }
        stretch
      />
    </AgxSpacerV2>
  );

  return (
    <>
      <FormDetailsHeader
        id="contract-signing-panel-header"
        formName="Contract"
        detail={headerDetail}
        currentTab={currentTab}
        onBack={() => dispatch(selectContract(null))}
      />

      <AgxColumn veryLargeGap fill centered>
        <AgxHeader size={2} centered>
          {addressLineOne},
          <br />
          {addressLineTwo}
        </AgxHeader>
        <AgxBodyText medium centered>
          {formatListOfStrings(getAllBuyerNames(allBuyers))} •{' '}
          {formatCurrencyWithConditionalDecimals(salePrice ?? 0, 2)}
        </AgxBodyText>
        <AgxRow centered justifyCenter extraClasses={'listing-imageCard'}>
          <div
            style={{
              backgroundImage: `url("${imageUrl || placeHolderImage}")`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              aspectRatio: '2/1',
            }}
            className={`imageCard-image`}
          >
            <div className="image-tag">
              <AgxLabel medium colour="brand-text-button-color">
                {state === CampaignDetailModelState.QLD
                  ? getEnumValue(
                      QldContractStage,
                      contractStage as QldContractStage
                    )
                  : getEnumValue(
                      NswContractStage,
                      contractStage as NswContractStage
                    )}
              </AgxLabel>
            </div>
          </div>
        </AgxRow>
        <AgxRow largeGap>
          {isContractExchange && <DownloadContractExchangeButton />}
          {envelopeSubmissionType !== EnvelopeSubmissionType.Email && (
            <EditContractButton />
          )}
          {!isMobile &&
            !approver &&
            envelopeSubmissionType !== EnvelopeSubmissionType.Email && (
              <SubmitContractButton />
            )}
          {!isMobile && approver && <DocuSignVendorButton />}
        </AgxRow>

        <AgxColumn fill centered extraClasses={'signatoriesContainer'}>
          {renderSignatories()}
        </AgxColumn>
        {!isContractExchange && (
          <FileList
            files={
              state === CampaignDetailModelState.NSW
                ? fileDownloadOptions.filter(
                    file => file.text !== DocumentTypesMap.TitleSearch
                  )
                : fileDownloadOptions
            }
            title={
              state === CampaignDetailModelState.QLD
                ? 'Contract Documents'
                : 'Sales Advice'
            }
          />
        )}
      </AgxColumn>

      {isMobile &&
        !approver &&
        envelopeSubmissionType !== EnvelopeSubmissionType.Email && (
          <div className="campaignModalButtons">
            <SubmitContractButton />
          </div>
        )}
      {isMobile && approver && <DocuSignVendorButton />}

      {showVoidEditConfirmationModal && (
        <AgxSlideUpModal
          closeButton
          content={voidEditConfirmationDialogBody}
          desktop={!isMobile}
          onClose={() => setShowVoidEditConfirmationModal(false)}
        />
      )}

      {showSendVendorsConfirmationModal && (
        <AgxSlideUpModal
          closeButton
          content={confirmSendToVendorsDialogBody}
          desktop={!isMobile}
          onClose={() => setShowSendVendorsConfirmationModal(false)}
        />
      )}

      {showExchangeConfirmationModal && (
        <AgxModal
          id="contract-exchange-confirmation-modal"
          title={
            showExchangeSuccessModal
              ? 'Contract Exchange Complete'
              : 'Exchange Contract'
          }
          titleIcon={exchangeModalIcon}
          content={exchangeConfirmationContent}
          actionButtons={exchangeConfirmationActions}
          actionButtonContainerStyle="withDivider"
          type={showExchangeSuccessModal ? 'dialog' : 'single'}
          variant="withoutDivider"
          minWidth="min(600px, calc(100vw - 32px))"
          setIsDialogOpen={isOpen => {
            if (!isOpen && !showExchangeSuccessModal) {
              closeExchangeModal();
            }
          }}
        />
      )}
    </>
  );
};

export default ContractRequestSigningPanel;
